export const CPU_WINDOW_MS = 10_000;
export const CPU_SHARE_LIMIT = 0.25;

interface Burst {
    readonly start: number;
    readonly duration: number;
}

export class LongTaskWindow {
    private bursts: Burst[] = [];
    private readonly startedAt: number;

    constructor(startedAt: number, private readonly windowMs: number = CPU_WINDOW_MS) {
        this.startedAt = startedAt;
    }

    add(start: number, duration: number): void {
        this.bursts.push({start, duration});
    }

    share(now: number): number | undefined {
        if (now - this.startedAt < this.windowMs) {
            return undefined;
        }
        const from = now - this.windowMs;
        this.bursts = this.bursts.filter(burst => burst.start + burst.duration > from);
        const busy = this.bursts.reduce((sum, burst) => sum + Math.min(burst.start + burst.duration, now) - Math.max(burst.start, from), 0);
        return Math.min(1, Math.max(0, busy / this.windowMs));
    }
}

export interface LongTaskMonitor {
    readonly share: () => number | undefined;
    readonly stop: () => void;
}

export const startLongTaskMonitor = (): LongTaskMonitor => {
    const tasks = new LongTaskWindow(performance.now());
    let observer: PerformanceObserver | undefined;
    try {
        if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
            observer = new PerformanceObserver(list => list.getEntries().forEach(entry => tasks.add(entry.startTime, entry.duration)));
            observer.observe({type: 'longtask'});
        }
    } catch {
        observer = undefined;
    }
    return {
        share: () => (observer ? tasks.share(performance.now()) : undefined),
        stop: () => observer?.disconnect(),
    };
};
